import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatDateTime } from "../../lib/format";

export default function AdminLicenses() {
    const [licenses, setLicenses] = useState([]);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        const params = filter === "all" ? {} : { status: filter };
        const { data } = await api.get("/admin/licenses", { params });
        setLicenses(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const filtered = licenses.filter((l) =>
        !q || l.key.toLowerCase().includes(q.toLowerCase()) || l.user?.email?.toLowerCase().includes(q.toLowerCase()) || l.product?.name?.toLowerCase().includes(q.toLowerCase())
    );

    const revoke = async (id) => {
        if (!window.confirm("Revoke this license? All activations will be deactivated.")) return;
        try {
            await api.post(`/admin/licenses/${id}/revoke`);
            toast.success("License revoked");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const reactivate = async (id) => {
        try {
            await api.post(`/admin/licenses/${id}/reactivate`);
            toast.success("License reactivated");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const regenerate = async (id) => {
        if (!window.confirm("Regenerate key? The customer will lose all activations and must use the new key on all machines.")) return;
        try {
            const { data } = await api.post(`/admin/licenses/${id}/regenerate`);
            toast.success(`New key: ${data.key}`);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const changeMax = async (id, current) => {
        const v = window.prompt("New maximum activations:", String(current));
        if (!v) return;
        const n = parseInt(v, 10);
        if (Number.isNaN(n) || n < 1 || n > 20) { toast.error("Must be 1-20"); return; }
        try {
            await api.patch(`/admin/licenses/${id}/max-activations`, { max_activations: n });
            toast.success("Maximum updated");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const deactivateActivation = async (activationId) => {
        if (!window.confirm("Deactivate this machine?")) return;
        try {
            await api.post(`/admin/activations/${activationId}/deactivate`);
            toast.success("Machine deactivated");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-licenses-page">
                <div>
                    <div className="label-mono mb-3">/ Licenses</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Licenses and activations</h1>
                </div>
                <div className="flex gap-2">
                    <Input placeholder="Search key, email or product…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-avx-surface border-avx-line h-11 w-72" data-testid="licenses-search" />
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="bg-avx-surface border-avx-line h-11 w-40" data-testid="licenses-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="revoked">Revoked</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <div className="panel p-10 text-center text-avx-muted">No licenses.</div>
                ) : filtered.map((lic) => (
                    <div key={lic.id} className="panel-elevated p-5" data-testid={`admin-license-${lic.id}`}>
                        <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
                            <div>
                                <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <span className="font-mono font-bold text-base">{lic.key}</span>
                                    <StatusBadge status={(lic.status === "active" && !lic.is_expired) ? "active" : "cancelled"} />
                                    {lic.is_trial && lic.is_expired && (
                                        <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-avx-danger/15 text-avx-danger border border-avx-danger/40" data-testid={`admin-trial-expired-${lic.id}`}>
                                            Trial expired
                                        </span>
                                    )}
                                    <span className="label-mono">{lic.active_count}/{lic.max_activations}</span>
                                </div>
                                <div className="text-sm text-avx-muted">
                                    <span className="text-avx-text">{lic.user?.name}</span> · <span className="font-mono text-xs">{lic.user?.email}</span> · <span className="text-avx-text">{lic.product?.name}</span>
                                </div>
                                <div className="font-mono text-[10px] text-avx-muted uppercase tracking-[0.18em] mt-1">
                                    Issued {formatDateTime(lic.created_at)}
                                </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button onClick={() => changeMax(lic.id, lic.max_activations)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-muted hover:text-avx-text border border-avx-line px-3 py-1 rounded-sm">Max</button>
                                <button onClick={() => regenerate(lic.id)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:bg-avx-blue/10 border border-avx-line px-3 py-1 rounded-sm">Regenerate</button>
                                {lic.status === "active" ? (
                                    <button onClick={() => revoke(lic.id)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:bg-avx-danger/10 border border-avx-danger/30 px-3 py-1 rounded-sm">Revoke</button>
                                ) : (
                                    <button onClick={() => reactivate(lic.id)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-success hover:bg-avx-success/10 border border-avx-success/30 px-3 py-1 rounded-sm">Reactivate</button>
                                )}
                            </div>
                        </div>
                        {lic.activations.filter(a => !a.deactivated_at).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-avx-line space-y-2">
                                {lic.activations.filter(a => !a.deactivated_at).map((a) => (
                                    <div key={a.id} className="flex items-center justify-between text-xs">
                                        <span className="font-mono">{a.machine_name} <span className="text-avx-muted">· {a.os} · {formatDateTime(a.activated_at)}</span></span>
                                        <button onClick={() => deactivateActivation(a.id)} className="text-avx-danger hover:underline font-mono uppercase tracking-[0.18em]">Deactivate</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </DashboardLayout>
    );
}
